import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { formatNumber } from "@/lib/utils";
import { formatProductSpecs } from "@/lib/format-specs";
import {
  Factory,
  Zap,
  Loader2,
  Layers,
  FileSpreadsheet,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";

interface CreateProductionOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: any;
  onSuccess?: () => void;
  onOpenImposition?: (detail: any) => void;
}

interface ItemParam {
  prepressCode: string;
  prepressPaperSize: string;
  prepressUps: number;
  allowancePercent: number;
}

export function CreateProductionOrderDialog({
  open,
  onOpenChange,
  order,
  onSuccess,
  onOpenImposition,
}: CreateProductionOrderDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const details: any[] = order?.orderDetails || [];

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [itemParams, setItemParams] = useState<Record<string, ItemParam>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ref ngăn populate lại khi order ref thay đổi mà dialog vẫn đang mở
  const hasPopulatedRef = useRef(false);

  // Initialize selected items and default prepress parameters
  useEffect(() => {
    if (!open || !details.length) return;
    if (hasPopulatedRef.current) return;
    hasPopulatedRef.current = true;

    // Default select items with remaining quantity to plan
    const needPlanIds = details
      .filter((d: any) => (d.remainingPlanQty ?? d.quantity) > 0)
      .map((d: any) => d.id);
    const initialSelected = needPlanIds.length > 0 ? needPlanIds : details.map((d: any) => d.id);
    setSelectedIds(initialSelected);

    const initialParams: Record<string, ItemParam> = {};
    details.forEach((d: any) => {
      // Không tự đặt mã bài bình / khổ giấy giả: dòng chưa bình để trống (máy chủ gom dòng theo bài bình thật)
      const code = d.prepressCode || "";
      const paperSize = d.prepressPaperSize || d.printLayout?.sheetSize || d.product?.printFormat || "";
      const ups = Number(d.prepressUps || d.product?.outs || 1);
      const allowance =
        d.allowancePercent !== null && d.allowancePercent !== undefined
          ? Number(d.allowancePercent)
          : 5;

      initialParams[d.id] = {
        prepressCode: code,
        prepressPaperSize: paperSize,
        prepressUps: ups > 0 ? ups : 1,
        allowancePercent: allowance,
      };
    });

    setItemParams(initialParams);
  }, [open, order]);

  // Reset ref khi dialog đóng
  useEffect(() => {
    if (!open) {
      hasPopulatedRef.current = false;
    }
  }, [open]);

  const handleParamChange = (id: string, field: keyof ItemParam, value: any) => {
    setItemParams((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleQuickCreatePO = async () => {
    if (selectedIds.length === 0) {
      toast.error("Vui lòng chọn ít nhất 1 sản phẩm để lập Lệnh Sản Xuất!");
      return;
    }

    setIsSubmitting(true);
    try {
      const payloadItems = selectedIds.map((id) => ({
        orderDetailId: id,
        prepressCode: itemParams[id]?.prepressCode?.trim() || undefined,
        prepressPaperSize: itemParams[id]?.prepressPaperSize?.trim() || undefined,
        prepressUps: Number(itemParams[id]?.prepressUps) || 1,
        allowancePercent: (() => {
          const raw: unknown = itemParams[id]?.allowancePercent;
          const n = Number(raw);
          return raw === undefined || raw === null || raw === "" || !Number.isFinite(n) ? 5 : n; // 0% là giá trị hợp lệ
        })(),
      }));

      const res = await trpcClient.productionOrders.createDirectFromOrder.mutate({
        orderId: order.id,
        orderDetailIds: selectedIds,
        items: payloadItems,
      });

      const created: any[] = Array.isArray(res) ? res : (res as any)?.created || [];
      const skipped: any[] = Array.isArray(res) ? [] : (res as any)?.skipped || [];
      const gangCount = created.filter((p: any) => p.poType === "GANG_RUN").length;
      toast.success(
        `Đã tạo ${created.length} Lệnh Sản Xuất từ Đơn Hàng ${order.orderId}` +
          (gangCount > 0 ? ` (${gangCount} lệnh in ghép theo bài bình)` : "") +
          "!",
        {
          description:
            skipped.length > 0
              ? `Bỏ qua ${skipped.length} dòng đã lên lệnh đủ: ${skipped.map((s: any) => s.productName).join(", ")}`
              : "BOM vật tư và công đoạn sản xuất đã được tự động nạp.",
        }
      );

      await queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({
        queryKey: ["order-production-orders-paginated", order.id],
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi lập Lệnh Sản Xuất tự động");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDetailedForm = () => {
    onOpenChange(false);
    navigate({
      to: "/production-orders/create",
      search: {
        salesOrderId: order.id,
        detailIds: selectedIds.join(","),
      } as any,
    });
  };

  const selectedCount = selectedIds.length;
  const totalQtyOrder = details
    .filter((d: any) => selectedIds.includes(d.id))
    .reduce((s: number, d: any) => s + (Number(d.quantity) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl sm:max-w-5xl w-full p-4 sm:p-6 max-h-[92vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
              <Factory className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>
                Lập Kế Hoạch / Lệnh Sản Xuất
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Đơn hàng:{" "}
                <span className="font-mono font-semibold text-slate-800">
                  {order?.orderId}
                </span>{" "}
                • Khách hàng:{" "}
                <span className="font-medium text-slate-800">
                  {order?.customer?.shortName || order?.customer?.name || "—"}
                </span>
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="my-2 flex-1 overflow-y-auto border border-slate-200 rounded-lg">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-slate-100 shadow-[inset_0_-1px_0_rgba(0,0,0,0.1)]">
              <TableRow>
                <TableHead className="w-10 text-center">
                  <Checkbox
                    checked={
                      selectedIds.length === details.length && details.length > 0
                    }
                    onCheckedChange={(c) =>
                      setSelectedIds(c ? details.map((d: any) => d.id) : [])
                    }
                  />
                </TableHead>
                <TableHead className="w-[30%]">Sản phẩm & Quy cách</TableHead>
                <TableHead className="w-[18%] text-right">Số lượng (Đặt/Còn)</TableHead>
                <TableHead className="w-[32%] text-center">Thông số Bài bình & Khổ in</TableHead>
                <TableHead className="w-[16%] text-center">BOM / Công đoạn</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {details.map((d: any) => {
                const isChecked = selectedIds.includes(d.id);
                const param = itemParams[d.id] || {
                  prepressCode: "",
                  prepressPaperSize: "",
                  prepressUps: 1,
                  allowancePercent: 5,
                };
                const qtyOrder = Number(d.quantity) || 0;
                const qtyPlan = Math.ceil(
                  qtyOrder * (1 + (param.allowancePercent || 5) / 100)
                );
                const estSheets = Math.ceil(qtyPlan / (param.prepressUps || 1));
                const remaining = d.remainingPlanQty ?? d.quantity;

                return (
                  <TableRow
                    key={d.id}
                    className={isChecked ? "bg-indigo-50/20" : "hover:bg-slate-50"}
                  >
                    <TableCell className="align-middle text-center">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(c) => {
                          if (c) setSelectedIds([...selectedIds, d.id]);
                          else setSelectedIds(selectedIds.filter((id) => id !== d.id));
                        }}
                      />
                    </TableCell>

                    <TableCell className="align-middle">
                      <div className="font-semibold text-xs text-slate-900 leading-tight">
                        {d.productCode && (
                          <span className="font-mono text-indigo-600 mr-1.5">
                            [{d.productCode}]
                          </span>
                        )}
                        {d.productName || d.product?.productName}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                        {formatProductSpecs(d.specs || d.product?.specs, d.product) || "—"}
                      </div>
                      {(d.printLayout?.layoutCode || d.prepressCode) && (
                        <div className="mt-1 text-[10px] text-slate-500">
                          Bài bình:{" "}
                          <span className="font-mono font-semibold text-indigo-700">
                            {d.printLayout?.layoutCode || d.prepressCode}
                          </span>
                        </div>
                      )}
                      {d.lsxId && (
                        <div className="mt-1">
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
                          >
                            <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> Đã có LSX
                          </Badge>
                        </div>
                      )}
                    </TableCell>

                    <TableCell className="text-right align-middle font-mono text-xs">
                      <div className="font-bold text-slate-900">
                        {formatNumber(qtyOrder)} {d.unit || "cái"}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        Đã lên: {formatNumber(d.plannedQty || 0)}
                      </div>
                      <div className="text-[11px] font-semibold text-amber-600">
                        Còn lại: {formatNumber(remaining)}
                      </div>
                    </TableCell>

                    <TableCell className="align-middle">
                      <div className="flex flex-col gap-1.5 bg-slate-50 p-2 rounded border border-slate-200/80">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-600 w-16 shrink-0">
                            Khổ in:
                          </span>
                          <Input
                            className="h-7 text-xs font-mono py-0 px-2 bg-white flex-1 min-w-0"
                            placeholder="vd: 650x860"
                            value={param.prepressPaperSize}
                            onChange={(e) =>
                              handleParamChange(d.id, "prepressPaperSize", e.target.value)
                            }
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-600 w-16 shrink-0">
                            Số con (Ups):
                          </span>
                          <Input
                            type="number"
                            min={1}
                            className="h-7 text-xs font-mono text-center py-0 px-1 bg-white w-16 shrink-0"
                            value={param.prepressUps}
                            onChange={(e) =>
                              handleParamChange(
                                d.id,
                                "prepressUps",
                                Math.max(1, parseInt(e.target.value) || 1)
                              )
                            }
                          />
                          <span className="text-[11px] text-slate-500">
                            Bù hao:
                          </span>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            className="h-7 text-xs font-mono text-center py-0 px-1 bg-white w-14 shrink-0"
                            value={param.allowancePercent}
                            onChange={(e) =>
                              handleParamChange(
                                d.id,
                                "allowancePercent",
                                Math.max(0, parseInt(e.target.value) || 0)
                              )
                            }
                          />
                          <span className="text-[11px] text-slate-500">%</span>
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-slate-600 pt-0.5 border-t border-slate-200">
                          <span>
                            Chạy:{" "}
                            <strong className="text-indigo-700 font-mono">
                              {formatNumber(estSheets)}
                            </strong>{" "}
                            tờ in
                          </span>
                          {onOpenImposition && (
                            <button
                              type="button"
                              className="text-indigo-600 hover:text-indigo-800 text-[10px] font-medium flex items-center gap-0.5"
                              onClick={() => onOpenImposition(d)}
                            >
                              <Layers className="w-3 h-3" /> Chi tiết bài bình
                            </button>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="align-middle text-center">
                      <div className="flex flex-col gap-1 items-center">
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 font-medium"
                        >
                          {d.product?.productMaterials?.length || 1} Vật tư BOM
                        </Badge>
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-purple-50 text-purple-700 border-purple-200 font-medium"
                        >
                          {d.product?.productProcesses?.length || 5} Công đoạn
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between w-full pt-2 border-t border-slate-100 gap-2 flex-wrap">
          <div className="text-xs text-slate-600">
            Đã chọn:{" "}
            <span className="font-bold text-slate-900">
              {selectedCount}/{details.length}
            </span>{" "}
            mặt hàng • Tổng sản lượng:{" "}
            <span className="font-bold font-mono text-indigo-700">
              {formatNumber(totalQtyOrder)}
            </span>{" "}
            sản phẩm
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Hủy
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="text-slate-700 border-slate-300 hover:bg-slate-50 gap-1 text-xs"
              onClick={handleOpenDetailedForm}
              disabled={isSubmitting || selectedCount === 0}
            >
              <ExternalLink className="h-3.5 w-3.5" /> Mở Form Chi Tiết
            </Button>

            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold gap-1.5 text-xs shadow-xs"
              onClick={handleQuickCreatePO}
              disabled={isSubmitting || selectedCount === 0}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Đang tạo lệnh...
                </>
              ) : (
                <>
                  <Zap className="h-3.5 w-3.5" /> Tạo Lệnh SX 1-Chạm
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
